import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useQuestion} from '../../../../hooks/question';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {DoorBrandModel} from '../../../../types/door-brand';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/developer/door-brand';

export const DoorBrandEditScreen = memo(function DoorBrandEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const doorList = useSelector(state => state.control.doorList);
    const doorBrandList = useSelector(state => state.control.doorBrandList);

    const [selected, setSelected] = useState<DoorBrandModel>(null);
    const [name, setName] = useState('');
    const [openCommandSuffix, setOpenCommandSuffix] = useState('');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_camera_name'));

            return false;
        }

        return true;
    }, [t, name, failure]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const doorBrandModel: DoorBrandModel = {
            id: selected?.id || null,
            name,
            door_open_command_suffix: openCommandSuffix,
        };

        return rpc.updateDoorBrand(doorBrandModel)
            .then(() => {
                success(t(doorBrandModel.id ? 'door_brand_successfully_edited' : 'door_brand_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        selected?.id,
        name,
        openCommandSuffix,
        rpc,
        failure,
        success,
        onBackPressed,
    ]);

    const onDeletePressed = useCallback(() => {
        const filteredDoorList = doorList.filter(door => door.door_brand_id === selected.id);
        if (filteredDoorList.length !== 0) {
            if (filteredDoorList.length === 1) {
                failure(t('named_door_uses_this_brand__pls_update_the_door_then_try_again',{name:filteredDoorList[0].name}));
            } else {
                failure(t('named_door_and_others_uses_this_brand__pls_update_these_doors_then_try_again',{name:filteredDoorList[0].name, count:(filteredDoorList.length - 1)}));
            }

            return;
        }

        question(t('are_you_sure_you_want_to_delete'), () => {
            return rpc.deleteDoorBrand(selected.id)
                .then(() => {
                    success(t('door_brand_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, doorList, failure, onBackPressed, question, rpc, selected?.id, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: BACK_URL,
            },
        ], [t, selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const doorBrand = doorBrandList.find(doorBrandItem => doorBrandItem.id === params['id']);
        if (doorBrand == null) {
            return;
        }

        setSelected(doorBrand);
        setName(doorBrand.name);
        setOpenCommandSuffix(doorBrand.door_open_command_suffix);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='developer' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_door_brand_name')}
                            label={t('door_brand_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={200}
                            placeholder={t('write_open_door_command')}
                            label={t('open_door_command')}
                            value={openCommandSuffix}
                            setValue={setOpenCommandSuffix}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
